import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const ALL_CAMPAIGNS_OPTION_ID = 'all';
const CAMPAIGN_STORAGE_KEY = 'engagement_trends.selectedCampaignId';

interface EngagementCampaignFilterContextValue {
  selectedCampaignId: string;
  normalizedCampaignId?: string;
  setSelectedCampaignId: (campaignId: string) => void;
}

const EngagementCampaignFilterContext = createContext<EngagementCampaignFilterContextValue | null>(
  null
);

const getInitialCampaignId = (): string => {
  if (typeof window === 'undefined') {
    return ALL_CAMPAIGNS_OPTION_ID;
  }
  return window.localStorage.getItem(CAMPAIGN_STORAGE_KEY) ?? ALL_CAMPAIGNS_OPTION_ID;
};

export const EngagementCampaignFilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>(() => getInitialCampaignId());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(CAMPAIGN_STORAGE_KEY, selectedCampaignId);
  }, [selectedCampaignId]);

  const normalizedCampaignId =
    selectedCampaignId === ALL_CAMPAIGNS_OPTION_ID ? undefined : selectedCampaignId;

  const value = useMemo<EngagementCampaignFilterContextValue>(
    () => ({ selectedCampaignId, normalizedCampaignId, setSelectedCampaignId }),
    [selectedCampaignId, normalizedCampaignId]
  );

  return (
    <EngagementCampaignFilterContext.Provider value={value}>
      {children}
    </EngagementCampaignFilterContext.Provider>
  );
};

export const useEngagementCampaignFilter = (): EngagementCampaignFilterContextValue => {
  const context = useContext(EngagementCampaignFilterContext);
  if (!context) {
    throw new Error('useEngagementCampaignFilter must be used within EngagementCampaignFilterProvider');
  }
  return context;
};

